import { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Seo } from '@/components/Seo';
import { Progress } from '@/components/ui/progress';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowRight, Plus } from 'lucide-react';
import PassportWorldMap from '@/components/passport/PassportWorldMap';
import SurfProfileCard from '@/components/dashboard/SurfProfileCard';
import SetSurfLevelCard from '@/components/dashboard/SetSurfLevelCard';
import LastSurfTripCard from '@/components/dashboard/LastSurfTripCard';
import SurfingJourneyCard from '@/components/dashboard/SurfingJourneyCard';
import NextMilestoneCard from '@/components/dashboard/NextMilestoneCard';
import WhereNextCard from '@/components/dashboard/WhereNextCard';
import AchievementRow from '@/components/progression/AchievementRow';
import { ACHIEVEMENTS_BY_LEVEL, categoriesForLevel, mandatoryCountForLevel, type SurfLevel } from '@/lib/surf-progression';
import type { PassportEntryRow } from '@/lib/passport';

// A handful of real-world ISO2 codes -- purely to exercise PassportWorldMap
// visually in this dev-only preview, not real user data.
const MOCK_HIGHLIGHTED_ISO2 = new Set(['ID', 'PT', 'CR', 'FR', 'AU', 'ZA', 'MV']);
const MOCK_DESTINATION_COUNTS = new Map([
  ['ID', 4], ['PT', 3], ['CR', 2], ['FR', 1], ['AU', 2], ['ZA', 1], ['MV', 1],
]);
const MOCK_RECENT_COUNTRIES = [
  { countryId: 'id', countryName: 'Indonesia', destinationName: 'Krui' },
  { countryId: 'pt', countryName: 'Portugal', destinationName: 'Ericeira' },
  { countryId: 'cr', countryName: 'Costa Rica', destinationName: 'Santa Teresa' },
];

// Dev-only route for visually reviewing the "My Swell Breaks" V1 dashboard
// components in every state (empty/new-user, partially populated, level
// variations) without needing a real authenticated session or real Surf
// Passport data. Not linked from site nav. Safe to remove once the real
// dashboard (src/pages/MySwellBreaks.tsx) has been reviewed and approved.
// Same pattern as src/pages/dev/SeasonAbilityGraphDev.tsx.

// No linked destination -- exercises the graceful no-photo placeholder,
// which is the realistic case today (no UI path sets linked_destination_id).
const MOCK_ENTRY_NO_PHOTO: PassportEntryRow = {
  id: 'dev-mock-1',
  user_id: 'dev-mock-user',
  country_id: 'dev-mock-country',
  destination_name: 'Krui',
  destination_name_normalized: 'krui',
  month: 8,
  linked_destination_id: null,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  countries: { id: 'dev-mock-country', name: 'Indonesia', iso2: 'ID' },
  destinations: null,
};

// Linked to a real canonical destination (Ericeira) with a real hero image,
// to exercise the photo path with a genuine, already-existing asset -- not
// a fabricated image.
const MOCK_ENTRY_WITH_PHOTO: PassportEntryRow = {
  ...MOCK_ENTRY_NO_PHOTO,
  id: 'dev-mock-2',
  destination_name: 'Ericeira',
  destination_name_normalized: 'ericeira',
  linked_destination_id: 'dev-mock-ericeira',
  countries: { id: 'dev-mock-country-pt', name: 'Portugal', iso2: 'PT' },
  destinations: { hero_image_url: 'https://xignafvsbxplbqdodfap.supabase.co/storage/v1/object/public/destination-media/destination-media/IMG_7371.jpg' },
};

const SCENARIOS = ['New user (empty)', 'Partially populated', 'Near level-up'] as const;
type Scenario = (typeof SCENARIOS)[number];

const MySwellBreaksDev = () => {
  const [scenario, setScenario] = useState<Scenario>('Partially populated');
  const [level, setLevel] = useState<SurfLevel>('Intermediate');
  const [showTripPhoto, setShowTripPhoto] = useState(false);
  const [completedIds, setCompletedIds] = useState<Set<string>>(() => {
    const allIds = ACHIEVEMENTS_BY_LEVEL['Intermediate'].filter((a) => !a.optional).map((a) => a.id);
    return new Set(allIds.slice(0, Math.floor(allIds.length * 0.4)));
  });

  const applyScenario = (nextScenario: Scenario, nextLevel: SurfLevel) => {
    const allIds = ACHIEVEMENTS_BY_LEVEL[nextLevel].filter((a) => !a.optional).map((a) => a.id);
    const next =
      nextScenario === 'New user (empty)'
        ? []
        : nextScenario === 'Near level-up'
          ? allIds.slice(0, Math.max(allIds.length - 2, 0))
          : allIds.slice(0, Math.floor(allIds.length * 0.4));
    setCompletedIds(new Set(next));
  };

  const toggleAchievement = (id: string) => {
    setCompletedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const hasPassportData = scenario !== 'New user (empty)';

  return (
    <Layout>
      <Seo title="Dev: My Swell Breaks states" description="Component state preview." path="/dev/my-swell-breaks" noIndex />

      <section className="py-8 border-b border-border/50 bg-secondary/20">
        <div className="container mx-auto px-4 max-w-4xl space-y-4">
          <h1 className="font-serif text-xl text-foreground">Dev: My Swell Breaks component states</h1>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center max-w-full">
            <label className="flex items-center gap-2 text-sm">
              Scenario:
              <select
                className="border border-border rounded-md px-2 py-1 text-sm bg-background max-w-[calc(100vw-8rem)]"
                value={scenario}
                onChange={(e) => {
                  const next = e.target.value as Scenario;
                  setScenario(next);
                  applyScenario(next, level);
                }}
              >
                {SCENARIOS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm">
              Level:
              <select
                className="border border-border rounded-md px-2 py-1 text-sm bg-background max-w-[calc(100vw-8rem)]"
                value={level}
                onChange={(e) => {
                  const next = e.target.value as SurfLevel;
                  setLevel(next);
                  applyScenario(scenario, next);
                }}
              >
                {(['Absolute Beginner', 'Beginner', 'Intermediate', 'Advanced', 'Expert'] as SurfLevel[]).map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={showTripPhoto} onChange={(e) => setShowTripPhoto(e.target.checked)} />
              Last trip has a linked photo
            </label>
          </div>
        </div>
      </section>

      <section className="pt-8">
        <div className="container mx-auto px-4 max-w-4xl">
          {scenario === 'New user (empty)' ? (
            <SetSurfLevelCard onSelect={() => {}} />
          ) : (
            <SurfProfileCard displayName="Finn Turley" level={level} levelAchievedCount={completedIds.size} />
          )}
        </div>
      </section>

      {/* Mirrors the Surf Passport section markup in MySwellBreaks.tsx, with
          mock data, purely so it can be visually reviewed without a real
          session -- PassportWorldMap is the same real component the
          production page uses, not rebuilt. */}
      {scenario !== 'New user (empty)' && (
        <section className="mt-6 md:mt-8 border-y border-border/50 bg-secondary/30 py-8 md:py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Your Surf Passport</p>
            <p className="mt-1.5 font-serif text-2xl md:text-3xl text-foreground">7 countries. 14 destinations.</p>
            <p className="mt-1 text-sm text-muted-foreground">Everywhere surfing has taken you.</p>

            <div className="-mx-4 sm:mx-0 mt-5 md:mt-6">
              <PassportWorldMap highlightedIso2={MOCK_HIGHLIGHTED_ISO2} destinationCountsByIso2={MOCK_DESTINATION_COUNTS} />
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {MOCK_RECENT_COUNTRIES.map((record) => (
                <span key={record.countryId}>
                  <Badge variant="secondary" className="font-normal py-1.5 px-3">
                    {record.countryName} · {record.destinationName}
                  </Badge>
                </span>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Button size="sm" className="gap-1.5">
                <Plus className="h-3.5 w-3.5" />
                Log Surf Trip
              </Button>
              <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                View Passport
                <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </div>
          </div>
        </section>
      )}

      <section className="py-10">
        <div className="container mx-auto px-4 max-w-4xl space-y-8">
          {scenario !== 'New user (empty)' && (
            <>
              <LastSurfTripCard
                latestEntry={hasPassportData ? (showTripPhoto ? MOCK_ENTRY_WITH_PHOTO : MOCK_ENTRY_NO_PHOTO) : null}
              />
              <SurfingJourneyCard level={level} completedIds={completedIds} />
              <NextMilestoneCard level={level} completedIds={completedIds} />
              <WhereNextCard />
            </>
          )}
        </div>
      </section>

      {/* Achievement browser -- mirrors SurfingProgression.tsx's rendering
          for the currently selected level, so it can be visually checked
          without the real page's auth gate. */}
      <section className="py-10 border-t border-border/50">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="font-serif text-xl text-foreground mb-4">Achievement browser preview: {level}</h2>
          {(() => {
            const achievements = ACHIEVEMENTS_BY_LEVEL[level];
            const mandatoryCount = mandatoryCountForLevel(level);
            const completedCount = achievements.filter((a) => !a.optional && completedIds.has(a.id)).length;
            const progressPct = mandatoryCount > 0 ? Math.round((completedCount / mandatoryCount) * 100) : 0;
            const categories = categoriesForLevel(level);
            return (
              <>
                <div className="mb-6">
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="text-sm text-muted-foreground">
                      {completedCount} / {mandatoryCount}
                    </span>
                    <span className="text-sm text-muted-foreground tabular-nums">{progressPct}%</span>
                  </div>
                  <Progress value={progressPct} className="h-2" />
                </div>
                <Accordion type="multiple" defaultValue={[categories[0]]} className="space-y-2">
                  {categories.map((category) => {
                    const inCategory = achievements.filter((a) => a.category === category);
                    const doneInCategory = inCategory.filter((a) => completedIds.has(a.id)).length;
                    return (
                      <AccordionItem key={category} value={category} className="border border-border/50 rounded-lg px-4 bg-card">
                        <AccordionTrigger className="hover:no-underline">
                          <span className="flex items-center gap-2 text-sm sm:text-base">
                            {category}
                            <span className="text-xs text-muted-foreground tabular-nums font-normal">
                              {doneInCategory}/{inCategory.length}
                            </span>
                          </span>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div>
                            {inCategory.map((achievement) => (
                              <AchievementRow
                                key={achievement.id}
                                achievement={achievement}
                                completed={completedIds.has(achievement.id)}
                                onToggle={() => toggleAchievement(achievement.id)}
                              />
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </>
            );
          })()}
        </div>
      </section>
    </Layout>
  );
};

export default MySwellBreaksDev;
